import { useState, useContext } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'src/components/ui';
import { useMsal } from '@azure/msal-react';
import { useHistory } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';

import UserIcon from './UserIcon';
import { AuthContext } from '../context/AuthContext';
import { setLanguage, isCurrentLanguage } from '../i18n';
import { useDarkMode } from '../context/DarkModeContext';
import Icon from 'src/components/Icon';
import { getOrgUnitAccess } from 'src/util/orgUnitAccess';

const LANGS = ['en', 'es'];

export function UserMenuItems({ showHeader = true, showFaq = true }) {
  const { instance: msalInstance } = useMsal();
  const history = useHistory();
  const auth = useContext(AuthContext);
  const user = auth?.user;
  const userInfo = user?.user;
  const permissions = user?.permissions ?? {};

  const orgUnitId = userInfo?.actualOrganizationalUnitId ?? userInfo?.organizationalUnit?.id;
  const orgUnitName = userInfo?.organizationalUnit?.name;
  const { canView: canViewInstitution, canManage: canManageOrgUnits } = getOrgUnitAccess(permissions);

  const [currentLang, setCurrentLang] = useState(() => LANGS.find(l => isCurrentLanguage(l)) ?? 'en');
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  const handleLanguage = lang => {
    setLanguage(lang);
    setCurrentLang(lang);
  };

  const handleLogout = () => {
    history.push('/login?skipRedirect=1');
    auth.logout({ msalInstance });
  };

  const toggles = (
    <>
      <div className="flex items-center">
        <span className="small text-muted">
          <Trans>Language</Trans>
        </span>
        <div className="btn-group btn-group-sm ml-auto">
          {LANGS.map(lang => (
            <button
              key={lang}
              className={`btn ${currentLang === lang ? 'btn-primary' : 'btn-outline-secondary'}`}
              style={{ minWidth: '2.5rem', fontWeight: 600, fontSize: '0.75rem' }}
              onClick={() => handleLanguage(lang)}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center mt-2">
        <span className="small text-muted">
          <Trans>Theme</Trans>
        </span>
        <div className="btn-group btn-group-sm ml-auto">
          <button
            type="button"
            className={`btn ${!isDarkMode ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ minWidth: '2.5rem' }}
            onClick={e => {
              e.stopPropagation();
              if (isDarkMode) toggleDarkMode();
            }}
            title="Light mode"
          >
            <Icon icon="Sun" />
          </button>
          <button
            type="button"
            className={`btn ${isDarkMode ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ minWidth: '2.5rem' }}
            onClick={e => {
              e.stopPropagation();
              if (!isDarkMode) toggleDarkMode();
            }}
            title="Dark mode"
          >
            <Icon icon="Moon" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {showHeader ? (
        <div className="px-3 pt-3 pb-2">
          <div className="flex items-center gap-3 mb-3">
            <UserIcon size="44px" user={userInfo} color={auth?.avatarColor} onClick={() => history.push('/profile')} />
            <div style={{ minWidth: 0 }}>
              <div className="font-semibold" style={{ lineHeight: 1.2 }}>
                {userInfo?.firstName} {userInfo?.lastName}
              </div>
              <div className="text-muted small truncate">{user?.email}</div>
              {orgUnitName && <div className="small uppercase">{orgUnitName}</div>}
            </div>
          </div>
          {toggles}
        </div>
      ) : (
        <div className="px-3 pt-2 pb-2">{toggles}</div>
      )}

      <DropdownItem divider className="my-0" />

      <div>
        <DropdownItem className="user-dropdown-item flex align-center justify-center" onClick={() => history.push('/profile')}>
          <Icon icon="User" className="mr-2 text-muted" />
          <span>
            <Trans>My Account</Trans>
          </span>
        </DropdownItem>

        {canViewInstitution && orgUnitId && (
          <DropdownItem
            className="user-dropdown-item align-center flex justify-center"
            // Managers keep the settings editor, which opens the unit they are working as and edits it in
            // full; everyone else gets their own unit on the page built for them.
            onClick={() => history.push(canManageOrgUnits ? `/system-settings/organizational-units?orgUnit=${orgUnitId}` : '/my-institution')}
          >
            <Icon icon="Building" className="mr-2 text-muted" />
            <Trans>My Institution</Trans>
          </DropdownItem>
        )}

        {showFaq && (
          <DropdownItem className="user-dropdown-item align-center flex justify-center" onClick={() => history.push('/faq')}>
            <Icon icon="Question" className="mr-2 text-muted" />
            <Trans>Frequently Asked Questions</Trans>
          </DropdownItem>
        )}

        {user?.impersonator && (
          <>
            <DropdownItem divider className="my-0" />
            <DropdownItem className="user-dropdown-item align-center flex justify-center" onClick={() => auth.stopImpersonation()}>
              <Icon icon="Detective" className="mr-2 text-warning" />
              <Trans>Stop impersonating</Trans>
            </DropdownItem>
          </>
        )}

        <DropdownItem divider className="my-0" />
        <DropdownItem className="user-dropdown-item text-danger align-center flex justify-center" onClick={handleLogout}>
          <Icon icon="SignOut" className="mr-2 text-danger" />
          <Trans>Log Out</Trans>
        </DropdownItem>
      </div>
    </>
  );
}

function UserDropdown({ hideCaret = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const auth = useContext(AuthContext);
  const userInfo = auth?.user?.user;

  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen(o => !o)} className="user-nav-dropdown">
      <DropdownToggle tag="span" nav caret={!hideCaret} className="badged-icon nav-icon">
        <UserIcon sx={{ cursor: 'pointer' }} size="30px" user={userInfo} color={auth?.avatarColor} />
      </DropdownToggle>

      <DropdownMenu end className="user-dropdown-menu p-0 shadow">
        <UserMenuItems showHeader={true} />
      </DropdownMenu>
    </Dropdown>
  );
}

export default UserDropdown;
